import type { ReactNode } from "react";
import { Box, Chip, Stack, Typography, type ChipProps } from "@mui/material";
import { crDateOf, daysBetween, formatCRC, Money, type LocalDate } from "@despejo/domain";
import type { IconName } from "./icons.js";

/** A Material Symbols (outlined) icon; the font is loaded by the app. */
export function Icon({ name, size = 24, fill = false, label }: { name: IconName; size?: number; fill?: boolean; label?: string }) {
  return (
    <Box
      component="span"
      className="material-symbols-outlined"
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
      sx={{ fontSize: size, lineHeight: 1, verticalAlign: "middle", fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' ${size}` }}
    >
      {name}
    </Box>
  );
}

export type Tone = "error" | "warning" | "success" | "neutral";

const TONE_SX: Record<Tone, object> = {
  error: { bgcolor: "error.light", color: "error.dark" },
  warning: { bgcolor: "warning.light", color: "warning.dark" },
  success: { bgcolor: "success.light", color: "success.dark" },
  neutral: { bgcolor: "m3.secondaryContainer", color: "m3.onSecondaryContainer" },
};

/**
 * Status is never colour-only (ADR-0009): every chip has an icon and text.
 */
export function StatusChip({ tone, icon, label, size = "small" }: { tone: Tone; icon: IconName; label: string; size?: ChipProps["size"] }) {
  return (
    <Chip
      size={size}
      icon={<Icon name={icon} size={18} fill={tone !== "neutral"} />}
      label={label}
      sx={{ ...TONE_SX[tone], "& .MuiChip-icon": { color: "inherit", ml: "6px" } }}
    />
  );
}

const FILING: Record<string, { tone: Tone; icon: IconName; label: string }> = {
  NOT_FILED: { tone: "neutral", icon: "pending", label: "Sin presentar" },
  FILED: { tone: "success", icon: "check_circle", label: "Presentada" },
  FILED_LATE: { tone: "warning", icon: "history", label: "Presentada tarde" },
  AMENDED: { tone: "success", icon: "edit_document", label: "Rectificada" },
  NOT_APPLICABLE: { tone: "neutral", icon: "block", label: "No aplica" },
  WAIVED: { tone: "neutral", icon: "do_not_disturb_on", label: "Dispensada" },
};
const PAYMENT: Record<string, { tone: Tone; icon: IconName; label: string }> = {
  NOT_DUE: { tone: "neutral", icon: "money_off", label: "Sin pago" },
  PENDING: { tone: "neutral", icon: "payments", label: "Pago pendiente" },
  PARTIALLY_PAID: { tone: "warning", icon: "timelapse", label: "Pago parcial" },
  PAID: { tone: "success", icon: "paid", label: "Pagada" },
  PAID_LATE: { tone: "warning", icon: "history", label: "Pagada tarde" },
  PAYMENT_ARRANGEMENT: { tone: "warning", icon: "event_repeat", label: "Arreglo de pago" },
};

export function FilingChip({ status }: { status: string }) {
  const s = FILING[status] ?? { tone: "neutral" as const, icon: "help", label: status };
  return <StatusChip {...s} />;
}
export function PaymentChip({ status }: { status: string }) {
  const s = PAYMENT[status] ?? { tone: "neutral" as const, icon: "help", label: status };
  return <StatusChip {...s} />;
}

export function MoneyText({ amount, currency = "CRC", variant = "body1" }: { amount: string | number | null; currency?: "CRC" | "USD" | "EUR"; variant?: "body1" | "body2" | "h5" | "h4" }) {
  if (amount === null) return <Typography variant={variant} color="text.secondary">—</Typography>;
  return (
    <Typography variant={variant} sx={{ fontVariantNumeric: "tabular-nums" }}>
      {formatCRC(Money.fromDb(amount, currency))}
    </Typography>
  );
}

export function formatCRCText(amount: string | number): string {
  return formatCRC(Money.fromDb(amount));
}

const DATE_FMT = new Intl.DateTimeFormat("es-CR", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

export function formatDateEs(date: LocalDate): string {
  return DATE_FMT.format(new Date(`${date}T00:00:00Z`));
}

/** "15 oct 2026 · en 20 días" / "· vence hoy" / "· hace 3 días" (CR time). */
export function DeadlineText({ date, now = new Date(), overdue }: { date: LocalDate; now?: Date; overdue?: boolean }) {
  const d = daysBetween(crDateOf(now), date);
  const rel = d === 0 ? "vence hoy" : d === 1 ? "mañana" : d > 1 ? `en ${d} días` : d === -1 ? "venció ayer" : `hace ${-d} días`;
  return (
    <Typography variant="body2" color={overdue ? "error" : d <= 3 ? "text.primary" : "text.secondary"} sx={{ fontWeight: overdue || d <= 3 ? 500 : 400 }}>
      {formatDateEs(date)} · {rel}
    </Typography>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", mb: 3 }}>
      <Box>
        <Typography variant="h2" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Stack direction="row" spacing={1}>{actions}</Stack>}
    </Stack>
  );
}

export function EmptyState({ icon, title, body, action }: { icon: IconName; title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <Stack spacing={1.5} sx={{ alignItems: "center", textAlign: "center", py: 6, px: 2, color: "text.secondary" }}>
      <Icon name={icon} size={48} />
      <Typography variant="h6" color="text.primary">
        {title}
      </Typography>
      {body && <Typography variant="body2" sx={{ maxWidth: 420 }}>{body}</Typography>}
      {action}
    </Stack>
  );
}

/** "DESPEJO" wordmark: black on white / white on black via currentColor. */
export function Wordmark({ size = 20 }: { size?: number }) {
  return (
    <Typography component="span" sx={{ fontWeight: 800, fontSize: size, letterSpacing: "0.12em", fontStretch: "115%" }}>
      DESPEJO
    </Typography>
  );
}
